import { useId, useState } from 'react';

/**
 * The published password rule (F-2). Length is the whole rule: no forced symbols or capitals, which
 * NIST SP 800-63B advises against. The server enforces the same numbers
 * (`auteur_cloud/api/routes/auth.py` PASSWORD_MIN_LENGTH / PASSWORD_MAX_LENGTH).
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 72;

/**
 * Counted the way the server counts: surrounding spaces are trimmed (as at sign-in), the minimum is in
 * characters (code points, so an emoji is one), and the maximum is GoTrue's 72-byte limit in UTF-8.
 */
// Exactly the characters Python's str.strip() removes (str.isspace), which the server applies. JavaScript's
// trim() differs (it strips U+FEFF and keeps U+001C..U+001F and U+0085), so it is not used here.
const PY_SPACE = '\\t\\n\\v\\f\\r\\u001c-\\u001f \\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const PY_STRIP = new RegExp(`^[${PY_SPACE}]+|[${PY_SPACE}]+$`, 'g');

export function serverTrim(value: string): string {
  return value.replace(PY_STRIP, '');
}

export function passwordProblem(value: string): string {
  const trimmed = serverTrim(value);
  const characters = [...trimmed].length;
  if (characters < PASSWORD_MIN_LENGTH) {
    const missing = PASSWORD_MIN_LENGTH - characters;
    return `${missing} more character${missing === 1 ? '' : 's'} needed`;
  }
  if (/[\uD800-\uDFFF]/u.test(trimmed)) return 'This password contains characters that cannot be used';
  if (new TextEncoder().encode(trimmed).length > PASSWORD_MAX_LENGTH) {
    return 'Too long: use a shorter password (accented letters and emoji count as more than one)';
  }
  return '';
}

export function passwordAcceptable(value: string): boolean {
  return passwordProblem(value) === '';
}

/**
 * A password input with a show/hide toggle (F-3) and, for a new password, the rule shown next to the
 * field with a live hint (F-2). The value is held by the caller and never stored anywhere else.
 */
export function PasswordField({
  id, label, value, onChange, autoComplete, disabled = false, showRules = false, name,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'new-password' | 'current-password';
  disabled?: boolean;
  /** True for a password being chosen: shows the rule and a live hint. */
  showRules?: boolean;
  name?: string;
}) {
  const [visible, setVisible] = useState(false);
  const hintId = useId();
  const problem = showRules ? passwordProblem(value) : '';
  return (
    <div className="password-field">
      <label htmlFor={id}>{label}</label>
      <div className="password-field-row">
        <input
          id={id}
          name={name}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={showRules ? PASSWORD_MIN_LENGTH : undefined}
          value={value}
          disabled={disabled}
          aria-describedby={showRules ? hintId : undefined}
          spellCheck={false}
          autoCapitalize="off"
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="password-toggle secondary"
          aria-controls={id}
          aria-pressed={visible}
          disabled={disabled}
          onClick={() => setVisible(!visible)}
        >
          {visible ? 'Hide' : 'Show'}
          <span className="visually-hidden"> password</span>
        </button>
      </div>
      {showRules ? (
        <p id={hintId} className="password-rules" aria-live="polite">
          At least {PASSWORD_MIN_LENGTH} characters (spaces at the start or end don’t count). Any letters,
          numbers, spaces or symbols; a short phrase such as “purple kettle on the moon” works well.
          {value ? <> <strong>{problem || 'Looks good.'}</strong></> : null}
        </p>
      ) : null}
    </div>
  );
}
